/**
 * The camera's look along the ladder (WORKBOARD §9.4, E4's "vehicle camera look"; decision 49: one
 * world, each rung a camera distance; decision 56: L0 over the shoulder now, first person the
 * target). The design lane's half of the vehicle camera path: **what the camera looks like at each
 * distance**, as stops on one path, never modes. Lane A owns the path itself — its state, its input,
 * what may move along it (decision 31) — and reads this table.
 *
 * A stop fixes: how far the camera stands from the subject (in the subject's own lengths near it,
 * in people at the commander's distances, in planet radii at the orbit), how steeply it looks down,
 * where it looks (the subject, or the planet's centre at the orbit), its field of view, the HUD it
 * carries and the post effect (the diorama's tilt-shift, Toy Soldiers). Between stops everything
 * eases — the distance in log space, so a zoom feels the same at every rung.
 *
 * Two paths: a person (eye → shoulder → follow → squad → diorama → orbit) and a vehicle (sight →
 * hatch → chase → squad → diorama → orbit). They share the commander's stops and the orbit.
 * The shoulder stop is the action host's camera exactly (scaleProfile: eye 1.0, back 3.2, up 0.55,
 * right 0.75 at a 1.1-tall person; FOV 55).
 *
 * Design reference: design_sheets/camera.html
 */
import * as THREE from 'three';

export type HudPart = 'reticle' | 'sight' | 'ammo' | 'health' | 'speed' | 'markers' | 'selection' | 'objectives' | 'map' | 'planets';
/** The rungs by their confirmed names (decision 27): Ground (L0), Command (L1), Theater (L2). */
export type Rung = 'ground' | 'command' | 'theater';

export interface CameraStop {
  name: string;
  rung: Rung;
  /** Distance from the look-at point: `d` in `per` units (the subject's length, a person, or the planet's radius). */
  d: number;
  per: 'subject' | 'person' | 'radius';
  /** How steeply it looks down, radians (0 level, π/2 straight down). */
  pitch: number;
  /** The look-at point's offset: up from the feet and right of the subject, in the subject's lengths. */
  rise: number;
  side: number;
  /** Vertical field of view, degrees. */
  fov: number;
  /** 0: looks at the subject; 1: at the planet's centre (the orbit). */
  focus: number;
  /** The diorama's tilt-shift, 0–1. */
  tilt: number;
  hud: HudPart[];
}

const P = 1.1;   // the classic person the action constants are written for (scaleProfile: × k)
/** A person's path: first person (the target), over the shoulder (today), then out to the commander and the orbit. */
export const PERSON_PATH: CameraStop[] = [
  { name: 'eye', rung: 'ground', d: 0.02, per: 'subject', pitch: 0, rise: 1.0 / P, side: 0, fov: 70, focus: 0, tilt: 0, hud: ['reticle', 'ammo', 'health'] },
  { name: 'shoulder', rung: 'ground', d: 3.2 / P, per: 'subject', pitch: 0, rise: (1.0 + 0.55) / P, side: 0.75 / P, fov: 55, focus: 0, tilt: 0, hud: ['reticle', 'ammo', 'health', 'markers'] },
  { name: 'follow', rung: 'ground', d: 9, per: 'subject', pitch: 0.32, rise: 0.7, side: 0, fov: 52, focus: 0, tilt: 0, hud: ['health', 'markers', 'objectives'] },
  { name: 'squad', rung: 'command', d: 45, per: 'person', pitch: 0.72, rise: 0.5, side: 0, fov: 40, focus: 0, tilt: 0.45, hud: ['markers', 'selection', 'objectives'] },
  { name: 'diorama', rung: 'command', d: 230, per: 'person', pitch: 0.95, rise: 0, side: 0, fov: 30, focus: 0, tilt: 1, hud: ['markers', 'selection', 'objectives', 'map'] },
  { name: 'orbit', rung: 'theater', d: 2.6, per: 'radius', pitch: Math.PI / 2, rise: 0, side: 0, fov: 40, focus: 1, tilt: 0, hud: ['planets'] },
];
/** A vehicle's path: the gunner's sight, the commander standing in the hatch, the chase, then the shared stops. */
export const VEHICLE_PATH: CameraStop[] = [
  { name: 'sight', rung: 'ground', d: 0.02, per: 'subject', pitch: 0, rise: 0.46, side: -0.14, fov: 22, focus: 0, tilt: 0, hud: ['sight', 'ammo'] },            // beside the tube, not behind it
  { name: 'hatch', rung: 'ground', d: 0.3, per: 'subject', pitch: 0.1, rise: 0.72, side: 0.16, fov: 66, focus: 0, tilt: 0, hud: ['speed', 'health', 'markers'] },   // the commander's hatch, off the tube's line
  { name: 'chase', rung: 'ground', d: 2.6, per: 'subject', pitch: 0.24, rise: 0.45, side: 0, fov: 55, focus: 0, tilt: 0, hud: ['reticle', 'speed', 'health', 'markers'] },
  ...PERSON_PATH.slice(3),
];

export interface CameraScale {
  /** The subject's length in world units (a person's height, a hull's length). */
  subject: number;
  /** A person's height in world units. */
  person: number;
  /** The planet's radius. */
  radius: number;
}
export interface CameraLook {
  /** world units from the look-at point */
  distance: number;
  pitch: number;
  /** world units up from the feet / right of the subject */
  rise: number;
  side: number;
  fov: number;
  focus: number;
  tilt: number;
  /** how much each HUD part shows, 0–1 (parts fade across a segment) */
  hud: Partial<Record<HudPart, number>>;
  /** the nearest stop and the rung it belongs to */
  stop: string;
  rung: Rung;
}

const smooth = (x: number) => x * x * (3 - 2 * x);
function worldDistance(s: CameraStop, sc: CameraScale): number {
  return s.d * (s.per === 'subject' ? sc.subject : s.per === 'person' ? sc.person : sc.radius);
}

/**
 * The look at path position `s` (0 = the first stop, path.length − 1 = the last; fractions ease
 * between neighbours). Exact at every stop; continuous everywhere.
 */
export function cameraLook(path: readonly CameraStop[], s: number, sc: CameraScale): CameraLook {
  const x = Math.max(0, Math.min(path.length - 1, s));
  const i = Math.min(path.length - 2, Math.floor(x));
  const a = path[i], b = path[i + 1], k = smooth(x - i);
  const lerp = (p: number, q: number) => p + (q - p) * k;
  const da = worldDistance(a, sc), db = worldDistance(b, sc);
  const hud: Partial<Record<HudPart, number>> = {};
  for (const h of a.hud) hud[h] = (hud[h] ?? 0) + (1 - k);
  for (const h of b.hud) hud[h] = (hud[h] ?? 0) + k;
  const near = k < 0.5 ? a : b;
  return {
    distance: Math.exp(lerp(Math.log(da), Math.log(db))),
    pitch: lerp(a.pitch, b.pitch),
    rise: lerp(a.rise, b.rise) * sc.subject,
    side: lerp(a.side, b.side) * sc.subject,
    fov: lerp(a.fov, b.fov),
    focus: lerp(a.focus, b.focus),
    tilt: lerp(a.tilt, b.tilt),
    hud, stop: near.name, rung: near.rung,
  };
}

const _t = new THREE.Vector3(), _r = new THREE.Vector3(), _f = new THREE.Vector3();
/**
 * Place a camera for a look: the subject stands at `feet` with `up` (the ground normal) and faces
 * `forward`; the planet's centre is `centre`. Sets position, orientation (up = the ground's normal
 * near the subject, the planet's at the orbit) and FOV.
 */
export function placeCamera(camera: THREE.PerspectiveCamera, look: CameraLook, feet: THREE.Vector3, up: THREE.Vector3, forward: THREE.Vector3, centre: THREE.Vector3): void {
  _f.copy(forward).addScaledVector(up, -forward.dot(up)).normalize();
  _r.crossVectors(_f, up).normalize();
  // the look-at point near the subject, sliding to the planet's centre at the orbit
  const near = _t.copy(feet).addScaledVector(up, look.rise).addScaledVector(_r, look.side);
  // back along the facing and up by the pitch
  const back = new THREE.Vector3().copy(_f).multiplyScalar(-Math.cos(look.pitch)).addScaledVector(up, Math.sin(look.pitch));
  camera.position.copy(near).addScaledVector(back, look.distance);
  const target = near.clone().lerp(centre, look.focus);
  // screen-up eases from the ground's normal to the facing as the camera tips toward straight down (no roll flip)
  const tip = smooth(Math.max(0, Math.min(1, (look.pitch - 1.1) / (Math.PI / 2 - 1.1))));
  camera.up.copy(up).lerp(_f, tip).normalize();
  camera.lookAt(target.distanceTo(camera.position) < 1e-6 ? camera.position.clone().add(_f) : target);
  camera.fov = look.fov;
  camera.updateProjectionMatrix();
}
